import { ChangeDetectionStrategy, Component, ViewChild, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { SharedModule } from '@shared/shared.module';

const DIALOG_KEY = 'ads-dictionary-reason';

/** Limite padrão do texto (nota de recusa; o motivo da volta de versão aceita mais). */
const DEFAULT_MAX_LENGTH = 1000;

/** O que a modal pergunta. */
export interface DictionaryReasonDialogConfig {
  title: string;
  /** Contexto mostrado acima do campo (o que vai acontecer ao confirmar). */
  description: string;
  label: string;
  placeholder: string;
  confirmLabel: string;
  /** `danger` para recusar; `default` (navy) para as demais. */
  confirmTheme?: 'default' | 'danger';
  maxLength?: number;
}

/**
 * Modal de texto obrigatório da aba Dicionário (PBI 3423, DPA-DIC-DB): nota de "Recusar proposta" e motivo de "Voltar
 * para esta versão". `open(config)` devolve o texto (sem espaços nas pontas) ou `null` quando a pessoa cancela.
 */
@Component({
  selector: 'app-dictionary-reason-dialog',
  standalone: true,
  imports: [FormsModule, SharedModule],
  templateUrl: './dictionary-reason-dialog.component.html',
  styleUrl: './dictionary-reason-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DictionaryReasonDialogComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef?: DialogComponent;

  protected readonly dialogKey = DIALOG_KEY;
  protected readonly config = signal<DictionaryReasonDialogConfig | null>(null);
  protected readonly text = signal('');
  protected readonly maxLength = computed(() => this.config()?.maxLength ?? DEFAULT_MAX_LENGTH);

  private resolver: ((text: string | null) => void) | null = null;

  constructor(translateService: TranslateService) {
    super(translateService);
  }

  open(config: DictionaryReasonDialogConfig): Promise<string | null> {
    this.resolve(null);
    this.config.set(config);
    this.text.set('');
    return new Promise(resolve => {
      this.resolver = resolve;
      void this.dialogRef
        ?.open('', '', '', 'Cancelar', '', DIALOG_KEY, false, false)
        .then(() => this.resolve(null));
    });
  }

  onDialogClosed(): void {
    this.resolve(null);
  }

  close(): void {
    this.dialogRef?.close();
  }

  isFormValid(): boolean {
    const text = this.text().trim();
    return text.length > 0 && text.length <= this.maxLength();
  }

  submitAction(): void {
    if (!this.isFormValid()) return;
    this.resolve(this.text().trim());
    this.close();
  }

  private resolve(text: string | null): void {
    const resolver = this.resolver;
    this.resolver = null;
    resolver?.(text);
  }
}
